import { LOCALE_ID, NgModule, NO_ERRORS_SCHEMA, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './routing/app-routing.module';
import { AppComponent } from './app.component';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { WebpackTranslationLoader } from './webpack-translation-loader';
import { firstValueFrom } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import localeDeCh from '@angular/common/locales/de-CH';
import { CoreModule } from './core/core.module';
import { SharedModule } from './shared/shared.module';
import { ApiModule, Configuration, ConfigurationParameters } from './core/api';
import { StoreModule, MetaReducer } from '@ngrx/store';
import { reducers, SSCState } from './reducers';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { SessionService } from './core/services/session/session.service';
import { OfferEffects } from './core/states/offer/offer.effects';
import { ReactiveFormsModule } from '@angular/forms';
import { IdService } from './core/services/id.service';
import { LanguageService } from './core/services/language/language.service';
import { environment } from '../environments/environment';
import { BasicInsuranceEffects } from './core/states/offer/basic-insurance/effects';
import { MedicSearchEffects } from './core/states/medic-search/medic-search.effects';
import { VisanaUiModule } from 'visana-ui-library';

registerLocaleData(localeDeCh);

function initTranslationFactory(languageService: LanguageService): () => Promise<unknown> {
  return (): Promise<unknown> => firstValueFrom(languageService.initLanguage$());
}

export function apiConfigFactory(): Configuration {
  const params: ConfigurationParameters = {
    basePath: '/offer/api/public',
  };
  return new Configuration(params);
}

export const metaReducers: MetaReducer<SSCState>[] = [];

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  schemas: [NO_ERRORS_SCHEMA],
  imports: [
    BrowserModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      loader: { provide: TranslateLoader, useClass: WebpackTranslationLoader },
    }),
    StoreModule.forRoot(reducers, {
      initialState: new SessionService(new IdService()).getState(),
      runtimeChecks: {
        strictActionImmutability: true,
        strictStateImmutability: true,
      },
      metaReducers: metaReducers,
    }),
    EffectsModule.forRoot([OfferEffects, BasicInsuranceEffects, MedicSearchEffects]),
    StoreDevtoolsModule.instrument({
      name: 'NgRx SSC UI DevTools',
      logOnly: environment.production,
      connectInZone: true,
    }),
    CoreModule.forRoot(),
    ApiModule.forRoot(apiConfigFactory),
    SharedModule,
    ReactiveFormsModule,
    VisanaUiModule,
  ],
  providers: [
    provideAppInitializer(() => {
      const initializerFn = initTranslationFactory(inject(LanguageService));
      return initializerFn();
    }),
    { provide: LOCALE_ID, useValue: 'de-CH' },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
